/**
 * Module dependencies.
 */

import { usePoolInvestBankLinks } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import BigNumber from 'bignumber.js';
import { PiggyBank } from 'lucide-react';
import React, { useMemo } from 'react';

/**
 * `Props` type.
 */

type Props = {
  transactionId: string | number;
};

/**
 * Format a money value for the badge.
 */

function fmtMoney(value: string | null): string {
  return new BigNumber(value ?? '0').decimalPlaces(2).toFormat(2);
}

/**
 * Export `PoolLinkBadge` — feature-guarded entry point. Only mounts the inner
 * (query-backed) badge when the `poolInvest` feature is enabled.
 */

export function PoolLinkBadge(props: Props): JSX.Element | null {
  const enabled = useFeature('poolInvest');

  return enabled ? <PoolLinkBadgeInner {...props} /> : null;
}

/**
 * Shows where a bank movement has been allocated in an investment pool — a pill
 * mirroring the project-link badge (pool name · category · signed amount).
 */

function PoolLinkBadgeInner({ transactionId }: Props): JSX.Element | null {
  const { data: bankLinks } = usePoolInvestBankLinks();

  const links = useMemo(
    () => (bankLinks ?? []).filter(link => String(link.sourceEntryId) === String(transactionId)),
    [bankLinks, transactionId]
  );

  if (!links.length) {
    return null;
  }

  return (
    <div className={'flex flex-wrap items-center gap-1.5'}>
      {links.map(link => (
        <span
          className={'inline-flex items-center overflow-hidden rounded-md border border-border text-[11px] font-medium'}
          key={link.projectId + (link.category ?? '')}
        >
          <span className={'flex items-center gap-1 bg-[#f3eefb] px-2 py-0.5 text-[#6d43b8]'}>
            <PiggyBank className={'h-3 w-3'} />

            {link.projectName}
          </span>

          {link.category && (
            <span className={'border-l border-border bg-[#f6f6e7] px-2 py-0.5 text-[#6f6f1a]'}>{link.category}</span>
          )}

          <span className={'border-l border-border bg-muted px-2 py-0.5 text-foreground tabular-nums'}>
            {link.direction === 'out' ? '−' : '+'}
            {'€'}
            {fmtMoney(link.amountFiat)}
            {link.feeFiat && Number(link.feeFiat) > 0 ? ` (+€${fmtMoney(link.feeFiat)})` : ''}
          </span>
        </span>
      ))}
    </div>
  );
}
